import { InputElement } from '@chakra-ui/react';
import { MakePaymentForm, MakePaymentFormInput } from '@monetize/types/app';
import {
  formatCurrency,
  getCurrencyDecimals,
  getCurrencySymbol,
} from '@monetize/utils/core';
import { addDays } from 'date-fns/addDays';
import { parseISO } from 'date-fns/parseISO';
import { startOfDay } from 'date-fns/startOfDay';
import { useMemo } from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { useGetEarliestManualPaymentDate } from '../../../api/settingsService';
import {
  MCurrencySelect,
  MCustomNumberInput,
  MFormField,
  MFormFieldReadOnly,
  MGrid,
  MGridItem,
  MInput,
  MTextarea,
} from '../../../components/Monetize';
import { DatePicker } from '../../../components/Monetize/DatePicker/DatePicker';

interface CreatePaymentWithManualPaymentBase {
  allowPartialPayment?: boolean;
  mode?: 'CREATE' | 'UPDATE';
  amount?: number;
  currency?: string;
  /**
   * Lets the user pick the payment's currency. Only for account payments, which have no
   * invoice to inherit a currency from.
   */
  isCurrencyEditable?: boolean;
  control: Control<MakePaymentFormInput, unknown, MakePaymentForm>;
  errors: FieldErrors<MakePaymentFormInput>;
  isSaving: boolean;
}

interface CreatePaymentWithManualPaymentCreate extends CreatePaymentWithManualPaymentBase {
  allowPartialPayment?: boolean;
  mode?: 'CREATE';
  currency: string;
}

interface CreatePaymentWithManualPaymentUpdate extends CreatePaymentWithManualPaymentBase {
  allowPartialPayment?: false | never;
  mode: 'UPDATE';
  amount: number;
  currency: string;
  isCurrencyEditable?: false | never;
}

type CreatePaymentWithManualPaymentProps =
  | CreatePaymentWithManualPaymentCreate
  | CreatePaymentWithManualPaymentUpdate;

export const CreatePaymentWithManualPayment = ({
  allowPartialPayment = false,
  mode = 'CREATE',
  amount,
  currency,
  isCurrencyEditable = false,
  control,
  errors,
  isSaving,
}: CreatePaymentWithManualPaymentProps) => {
  const { data: earliestManualPaymentSettings } =
    useGetEarliestManualPaymentDate();

  const minDate = useMemo(() => {
    if (earliestManualPaymentSettings?.earliestManualPaymentDate) {
      const baseManualPaymentDate =
        earliestManualPaymentSettings?.earliestManualPaymentDate;

      return baseManualPaymentDate
        ? addDays(startOfDay(parseISO(baseManualPaymentDate)), 1)
        : undefined;
    }
  }, [earliestManualPaymentSettings]);

  return (
    <>
      <MGrid templateColumns="repeat(2, 1fr)" mt="4" gap={4}>
        {isCurrencyEditable && (
          <MGridItem colSpan={2}>
            <MFormField error={errors.currency} label="Currency" isRequired>
              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <MCurrencySelect isDisabled={isSaving} {...field} />
                )}
              />
            </MFormField>
          </MGridItem>
        )}
        <MGridItem colSpan={1}>
          {mode === 'CREATE' ? (
            <MFormField error={errors.amount} label="Amount" isRequired>
              <Controller
                name="amount"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <MCustomNumberInput
                    isDisabled={!allowPartialPayment || isSaving}
                    inputMode="decimal"
                    precision={getCurrencyDecimals(currency)}
                    onChange={(value) => onChange(value)}
                    value={value ?? undefined}
                    styleProps={{ paddingInlineStart: 6 }}
                    LeftElement={
                      <InputElement
                        placement="start"
                        w="4"
                        px="0"
                        pl="2"
                        pointerEvents="none"
                      >
                        {currency && getCurrencySymbol(currency)}
                      </InputElement>
                    }
                    {...field}
                  />
                )}
              />
            </MFormField>
          ) : (
            <MFormFieldReadOnly label="Amount">
              {formatCurrency(amount, { currency })}
            </MFormFieldReadOnly>
          )}
        </MGridItem>
        <MGridItem colSpan={1}>
          <MFormField
            error={errors.paymentDate}
            label="Payment Date"
            isRequired
          >
            <Controller
              name="paymentDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  isDisabled={
                    isSaving ||
                    !earliestManualPaymentSettings?.earliestManualPaymentDate
                  }
                  minDate={minDate}
                  readonlyContentProps={{
                    style: {
                      lineHeight: 2,
                    },
                  }}
                  {...field}
                />
              )}
            />
          </MFormField>
        </MGridItem>
      </MGrid>
      <MGrid mt="4" rowGap={4}>
        <MGridItem colSpan={1}>
          <MFormField label="Reference No.">
            <Controller
              name="referenceNo"
              control={control}
              defaultValue=""
              render={({ field: { value, ...rest } }) => (
                <MInput
                  placeholder="Check 1234"
                  isDisabled={isSaving}
                  value={value || ''}
                  {...rest}
                />
              )}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={1}>
          <MFormField label="Comments">
            <Controller
              name="description"
              control={control}
              defaultValue=""
              render={({ field: { ref, onBlur, value, ...rest } }) => (
                <MTextarea
                  placeholder="Reasons for manual payment"
                  disabled={isSaving}
                  value={value || ''}
                  {...rest}
                />
              )}
            />
          </MFormField>
        </MGridItem>
      </MGrid>
    </>
  );
};
